'use strict';

import React from 'react';
import { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Tabs, Tab, Table, Button, Row, Col, Form, OverlayTrigger, Tooltip, Stack } from 'react-bootstrap';
import Select from 'react-select';

import { Formik } from 'formik';
import * as yup from 'yup';

import AppContext from '../AppContext';
import useConfirmation from '../helper/Confirmation';
import useRequest from '../helper/useRequest';
import { formatDate, formatTime, getDateFromString } from '../helper/DateTimeFormater';
import { toast } from 'react-toastify';

import Loader from '../helper/Loader';
import TableHeader from '../helper/TableHeader';
import TimeslotEdit from './TimeslotEdit';

import AddContactperson from '../helper/Contactperson/AddContactpersonModal';
import EditContactperson from '../helper/Contactperson/EditContactpersonModal';

import MandatoryFieldNotice from '../helper/MandatoryFieldNotice';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    getFilteredRowModel,
} from '@tanstack/react-table'
import PersonModal from '../helper/Modal/PersonModal';

const columnHelper = createColumnHelper();

function SchoolOperator() {
    const { request } = useRequest();
    const { getSalutation } = useContext(AppContext);


    const [id, setId] = useState(0);
    const [business, setBusiness] = useState(false);
    const [contactPersons, setContactPersons] = useState([]);
    const [equipment, setEquipment] = useState([]);
    const [categories, setCategories] = useState([]);

    const [oldContactPerson, setOldContactPerson] = useState({});
    const [oldCategories, setOldCategories] = useState([]);
    const [oldEquipment, setOldEquipment] = useState([]);

    const [slots, setSlots] = useState([]);
    const [users, setUsers] = useState([]);

    
    const saveSchool = async (values) => {
        try {
            let result = await request("/Operator/ChangeOrganisation", values);
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                setOldContactPerson();
                toast.success("Die Schule wurde erfolgreich gespeichert.");
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    }

    const handleDeleteOrganisation = async (organisation) => {
        try {
            let result = await request("/Operator/DeleteOrganisation", { organisation });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                window.location.href = "/Operator/Schools";
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                let path = location.pathname.split("/");
                if (path.length < 4) return;

                let id = path[3];
                setId(id);
                let result = await request("/Operator/GetSchool", { id: id });
                let json = result.data;
                if (json.r != 1) {
                    if (json.message) {
                        toast.error(json.message);
                    }
                    else {
                        toast.error("Es ist ein Fehler aufgetreten.");
                    }
                }
                else {
                    setBusiness(json.school);
                    setContactPersons(json.contactPersons.map((item) => {
                        item.SalutationDescription = getSalutation(item.SalutationID);
                        return item;
                    }));
                    setOldContactPerson(json.oldContactPerson);
                    setSlots(json.slots.map(item => {
                        item.AppointmentDescription = formatDate(item.Appointment);
                        item.EquipmentDescription = item.Equipment.map((item) => { return item.Description; }).join("; ");

                        return item;
                    }));
                    setUsers(json.users.map((item) => {
                        item.SalutationDescription = getSalutation(item.SalutationID);
                        return item;
                    }));
                    setOldCategories(json.oldCategories);
                    setOldEquipment(json.oldEquipment);
                    setEquipment(json.equipment);
                    setCategories(json.categories);
                }
            }
            catch (ex) {
                console.error(ex);
                toast.error("Es ist ein Fehler aufgetreten.");
            }
        };
        fetchData();
    }, []);

    if (business === false) return <Loader />

    return (
        <>
            <h1>Schule ({business.OrganisationName})</h1>
            <Tabs
                defaultActiveKey="info"
                id="organisation-tabs"
                className="mb-3"
            >
                <Tab eventKey="info" title="Info">
                    <SchoolInfo
                        organisation={business}
                        contactPersons={contactPersons}
                        categories={categories}
                        equipment={equipment}
                        oldContactPerson={oldContactPerson}
                        oldCategories={oldCategories}
                        oldEquipment={oldEquipment}
                        save={saveSchool}
                        handleDeleteOrganisation={handleDeleteOrganisation}
                    />
                </Tab>
                <Tab eventKey="termine" title="Termine">
                    <Timeslots slots={slots} setSlots={setSlots} />
                </Tab>
                <Tab eventKey="benutzer" title="Benutzer">
                    <Users users={users} setUsers={setUsers} id={id} />
                </Tab>
                <Tab eventKey="ansprechpartner" title="Ansprechpartner">
                    <Contactpersons persons={contactPersons} setPersons={setContactPersons} id={id} />
                </Tab>
            </Tabs>
        </>
    )
}

function SchoolInfo({ organisation, contactPersons, equipment, categories, oldContactPerson, oldEquipment, oldCategories, save, handleDeleteOrganisation }) {
    const formRef = useRef(null);
    const { TypesOfSchool } = useContext(AppContext);

    const [contactPerson, setContactPerson] = useState([]);
    const [equipmentList, setEquipment] = useState([]);
    const [categorieList, setCategories] = useState([]);
    const [disabled, setDisabled] = useState(true);
    const confirmation = useConfirmation();

    const sortedOptions = useMemo(() => TypesOfSchool.sort((a, b) => {
        const nameA = a.Description.toUpperCase();
        const nameB = b.Description.toUpperCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
    }), [TypesOfSchool]);

    const abort = () => {
        setDisabled(true);
        formRef.current.setFieldValue("contactPerson", { value: oldContactPerson.ContactPersonID, label: oldContactPerson.FirstName + " " + oldContactPerson.LastName });
        formRef.current.setFieldValue("organisationName", organisation.OrganisationName);
        formRef.current.setFieldValue("street", organisation.Street);
        formRef.current.setFieldValue("houseNo", organisation.HouseNo);
        formRef.current.setFieldValue("additionToAddress", organisation.AdditionToAddress);
        formRef.current.setFieldValue("postCode", organisation.PostCode);
        formRef.current.setFieldValue("city", organisation.City);
        formRef.current.setFieldValue("expiration", organisation.ExpireTimeDefault);
        formRef.current.setFieldValue("equipment", oldEquipment.map((val) => { return { value: val.EquipmentID, label: val.Description } }));
        formRef.current.setFieldValue("categories", oldCategories.map((val) => { return { value: val.CategoryID, label: val.Description } }));
        formRef.current.setFieldValue("typeOfSchoolId", organisation.TypeOfSchoolID);
    };

    const handleSave = (values) => {
        setDisabled(true);
        save(values);
    }

    const confirmDelete = () => {
        confirmation.openModal("Organisation löschen", "Soll die Schule " + organisation.OrganisationName + " und alle zugehörigen Daten wirklich unwiderruflich gelöscht werden?", () => { handleDeleteOrganisation(organisation) }, "Ja", "Nein")
    };

    useEffect(() => {
        if (!organisation) return;

        setContactPerson(contactPersons.map((val) => { return { value: val.ContactPersonID, label: val.FirstName + " " + val.LastName } }));
        setEquipment(equipment.map((val) => { return { value: val.EquipmentID, label: val.Description } }));
        setCategories(categories.map((val) => { return { value: val.CategoryID, label: val.Description } }));

        formRef.current.setFieldValue("organisationName", organisation.OrganisationName);
        formRef.current.setFieldValue("street", organisation.Street);
        formRef.current.setFieldValue("houseNo", organisation.HouseNo);
        formRef.current.setFieldValue("additionToAddress", organisation.AdditionToAddress);
        formRef.current.setFieldValue("postCode", organisation.PostCode);
        formRef.current.setFieldValue("city", organisation.City);
        formRef.current.setFieldValue("expiration", organisation.ExpireTimeDefault);
        formRef.current.setFieldValue("equipment", oldEquipment.map((val) => { return { value: val.EquipmentID, label: val.Description } }));
        formRef.current.setFieldValue("categories", oldCategories.map((val) => { return { value: val.CategoryID, label: val.Description } }));
        formRef.current.setFieldValue("typeOfSchoolId", organisation.TypeOfSchoolID);

        if (oldContactPerson) {
            formRef.current.setFieldValue("contactPerson", { value: oldContactPerson.ContactPersonID, label: oldContactPerson.FirstName + " " + oldContactPerson.LastName });
        }
    }, [organisation]);

    return <Formik
        innerRef={(f) => formRef.current = f}
        validationSchema={yup.object().shape({})}
        onSubmit={(values) => {
            let organisationInfo = {
                OrganisationName: values.organisationName,
                Street: values.street,
                HouseNo: values.houseNo,
                AdditionToAddress: values.additionToAddress,
                PostCode: values.postCode,
                City: values.city,
                ExpireTimeDefault: values.expiration,
                TypeOfSchoolID: values.typeOfSchoolId
            };
            let data = {
                organisationId: organisation.OrganisationID,
                contactPerson: values.contactPerson.value,
                categories: values.categories.map((val) => { return val.value; }),
                equipment: values.equipment.map((val) => { return val.value; }),
                organisationInfo: organisationInfo
            };
            handleSave(data);
        }}
        initialValues={{
            organisationName: '',
            typeOfSchoolId: 'default',
            street: '',
            houseNo: '',
            additionToAddress: '',
            postCode: '',
            city: '',
            expiration: 0,
            equipment: [],
            categories: [],
        }}
    >
        {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            touched,
            isValid,
            errors
        }) => {
            return <Form noValidate onSubmit={handleSubmit}>
                <Stack gap={2} className="col-md-6 mt-3">
                    <Row>
                        <Col sm="12" md="6">
                            <Form.Group controlId="organisationName">
                                <Form.Label className="required">Name</Form.Label>
                                <Form.Control isInvalid={errors.organisationName && touched.organisationName} placeholder="" name="organisationName" value={values.organisationName} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                <Form.Control.Feedback type="invalid">
                                    {errors.organisationName}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col sm="12" md="6">
                            <Form.Group controlId="typeOfSchoolId" onBlur={handleBlur} onChange={handleChange} name="typeOfSchoolId">
                                <Form.Label className="required">Schulform</Form.Label>
                                <Form.Select enabled="true" disabled={disabled} value={values.typeOfSchoolId}>
                                    <option value='default' disabled>Bitte auswählen</option>
                                    {sortedOptions.map((option) => {
                                        return (
                                            <option key={option.TypeOfSchoolID} value={option.TypeOfSchoolID}>
                                                {option.Description}
                                            </option>
                                        );
                                    })}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm="12" md="8">
                            <Form.Group controlId="street">
                                <Form.Label className="required">Straße</Form.Label>
                                <Form.Control isInvalid={errors.street && touched.street} placeholder="" name="street" value={values.street} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                <Form.Control.Feedback type="invalid">
                                    {errors.street}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col sm="12" md="4">
                            <Form.Group controlId="houseNo">
                                <Form.Label className="required">Hausnummer</Form.Label>
                                <Form.Control isInvalid={errors.houseNo && touched.houseNo} placeholder="" name="houseNo" value={values.houseNo} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                <Form.Control.Feedback type="invalid">
                                    {errors.houseNo}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm="12" md="6">
                            <Form.Group controlId="additionToAddress">
                                <Form.Label>Addresszusatz</Form.Label>
                                <Form.Control isInvalid={errors.additionToAddress && touched.additionToAddress} placeholder="" name="additionToAddress" value={values.additionToAddress} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                <Form.Control.Feedback type="invalid">
                                    {errors.additionToAddress}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm="12" md="6">
                            <Form.Group controlId="postCode">
                                <Form.Label className="required">Postleitzahl</Form.Label>
                                <Form.Control isInvalid={errors.postCode && touched.postCode} placeholder="" name="postCode" value={values.postCode} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                <Form.Control.Feedback type="invalid">
                                    {errors.postCode}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col sm="12" md="6">
                            <Form.Group controlId="city">
                                <Form.Label className="required">Ort</Form.Label>
                                <Form.Control isInvalid={errors.city && touched.city} placeholder="" name="city" value={values.city} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                <Form.Control.Feedback type="invalid">
                                    {errors.city}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm="12" md="6">
                            <Form.Group controlId="expirationLabel">
                                <Form.Label className="required">Standard-Ablaufzeit für Termine (in Stunden vor Termin-Begin)</Form.Label>
                            </Form.Group>
                        </Col>
                        <Col sm="12" md="6">
                            <Form.Group controlId="equipmentLabel">
                                <Form.Label >Standard-Ausstattung</Form.Label>                               
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12" md="6">
                            <Form.Group controlId="expiration">                              
                                <Form.Control type="number" name="expiration" value={values.expiration} onBlur={handleBlur} onChange={handleChange} step="1" disabled={disabled}></Form.Control>
                            </Form.Group>
                        </Col>
                        <Col sm="12" md="6">
                            <Form.Group controlId="equipment">                         
                                <Select isMulti name="Equipment" options={equipmentList} value={values.equipment} classname="basic-multi-select" placeholder="Bitte ausw&auml;hlen" onChange={
                                    (values) => {
                                        let vals = values.map((v) => v);
                                        formRef.current.setFieldValue("equipment", vals);
                                    }
                                } isDisabled={disabled} />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group controlId="categories">
                        <Form.Label>NICHT erwünschte Betriebskategorien für neu zu erstellende Termine</Form.Label>
                        <Select isMulti name="categories" options={categorieList} value={values.categories} classname="basic-multi-select" placeholder="Bitte ausw&auml;hlen" onChange={
                            (values) => {
                                let vals = values.map((v) => v);
                                formRef.current.setFieldValue("categories", vals);
                            }
                        } isDisabled={disabled} />
                    </Form.Group>

                    <Form.Group as={Row} controlId="contactPerson">
                        <Col><Form.Label className="required">Ansprechpartner</Form.Label></Col>
                        <Select name="contactPerson" options={contactPerson} value={values.contactPerson} onChange={
                            (values) => {
                                formRef.current.setFieldValue("contactPerson", values);
                            }
                        } isDisabled={disabled} />
                    </Form.Group>

                    <Row>
                        <Col sm="6">
                            {disabled && <Button variant="primary" type="button" onClick={() => setDisabled(!disabled)}><i className="fa-solid fa-pen me-2"></i>Bearbeiten</Button>}
                            {!disabled && (
                                <>
                                    <Button variant="secondary" type="button" className="me-2" onClick={() => abort()}><i className="fa-solid fa-times me-2"></i>Abbrechen</Button>
                                    <Button variant="primary" type="submit"><i className="fa-solid fa-check me-2"></i>Speichern</Button>
                                </>
                            )}

                        </Col>
                        <Col sm="6" className="text-end">
                            {disabled && <Button variant="danger" type="button" onClick={() => confirmDelete()}><i className="fa-solid fa-trash me-2"></i>Löschen</Button>}
                        </Col>
                    </Row>
                </Stack>

                <MandatoryFieldNotice />
            </Form>
        }}
    </Formik>;
}

function Timeslots({ slots, setSlots }) {
    const confirmation = useConfirmation();
    const { request } = useRequest();

    const { getSalutation } = useContext(AppContext);
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [modal, setModal] = useState(false);
    const [timeslot, setTimeslot] = useState(false);

    const now = new Date();


    const deleteTimeslot = async (id) => {
        try {
            let result = await request("/Operator/DeleteTimeslot", { timeslotId: id });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                toast.success("Zeitsatz gelöscht.");

                setSlots(items => {
                    return items.filter(item => item.TimeslotID != id);
                });
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    };

    const columns = [
        columnHelper.accessor('AppointmentDescription', {
            header: () => <span>Datum</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('TimeStart', {
            header: () => <span>Startzeit</span>,
            cell: info => formatTime(info.getValue()),
        }),
        columnHelper.accessor('TimeEnd', {
            header: () => <span>Endzeit</span>,
            cell: info => formatTime(info.getValue()),
        }),
        columnHelper.accessor('Grade', {
            header: () => <span>Stufe</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Class', {
            header: () => <span>Klasse</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Room', {
            header: () => <span>Raum</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('EquipmentDescription', {
            header: () => <span>Ausstattung</span>,
            cell: info => info.getValue(),
            enableSorting: false,
        }),
        columnHelper.accessor('TimeslotID', {
            size: 95,
            header: () => <span></span>,
            cell: info => {
                let d = getDateFromString(info.row.original.Appointment + " " + info.row.original.TimeStart);
                if (d < now) return;

                if (info.row.original.BookedBusiness == 0) {
                    return (
                        <div className="text-end">
                            <OverlayTrigger overlay={<Tooltip>Bearbeiten</Tooltip>}>
                                <Button variant="primary" size="sm" onClick={() => { setModal(true); setTimeslot({ ...info.row.original }) }}>
                                    <i className="fa-solid fa-fw fa-pen"></i>
                                </Button>
                            </OverlayTrigger>
                            <OverlayTrigger overlay={<Tooltip>Löschen</Tooltip>}>
                                <Button variant="danger" size="sm" className="ms-1" onClick={() => confirmation.openModal("Wirklich löschen?", "Möchten Sie diesen Termin wirklich löschen?", () => { deleteTimeslot(info.getValue()) }, "Ja", "Nein")}>
                                    <i className="fa-solid fa-fw fa-trash"></i>
                                </Button>
                            </OverlayTrigger>
                        </div>
                    );
                }
                else {
                    return <div className="text-end">
                        <OverlayTrigger overlay={<Tooltip>Bearbeiten</Tooltip>}>
                            <Button variant="primary" size="sm" onClick={() => { setModal(true); setTimeslot({ ...info.row.original }) }}>
                                <i className="fa-solid fa-fw fa-pen"></i>
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger overlay={<Tooltip>Löschen</Tooltip>}>
                            <Button variant="danger" size="sm" className="ms-1" onClick={() => confirmation.openModal("Wirklich löschen?", "Möchten Sie diesen Termin wirklich löschen?", () => { deleteTimeslot(info.getValue()) }, "Ja", "Nein")}>
                                <i className="fa-solid fa-fw fa-trash"></i>
                            </Button>
                        </OverlayTrigger>
                    </div>
                }  
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ]

    const reactTable = useReactTable({
        data: slots || [],
        columns,
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const editTimeslot = async (timeslot) => {
        setSlots((items) => {
            return items.map((item) => {
                if (item.TimeslotID == timeslot.TimeslotID) {
                    item.Appointment = timeslot.Appointment;
                    item.TimeStart = timeslot.TimeStart;
                    item.TimeEnd = timeslot.TimeEnd;
                    item.Grade = timeslot.Grade;
                    item.Class = timeslot.Class;
                    item.Room = timeslot.Room;
                    item.Equipment = timeslot.Equipment;
                    item.Category = timeslot.Category;
                    item.ExpireTime = timeslot.ExpireTime;
                    item.ContactPersonSchool = timeslot.ContactPersonSchool;
                    item.ContactSchool = timeslot.ContactSchool;
                    item.ContactPersonOrganisation = timeslot.ContactPersonOrganisation;
                    item.ContactOrganisation = timeslot.ContactOrganisation;
                }

                return item;
            })
        });
    }

    return (
        <>
            <Table striped>
                <thead>
                    {reactTable.getHeaderGroups().map(headerGroup => (
                        <TableHeader key={headerGroup.id} table={reactTable} headerGroup={headerGroup} flexRender={flexRender} />
                    ))}
                </thead>
                <tbody>
                    {reactTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => {
                                return (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
            <TimeslotEdit timeslot={timeslot} open={modal} setOpen={setModal} editTimeslot={editTimeslot} />
        </>
    );
}

function Users({ users, setUsers, id }) {
    const { request } = useRequest();

    const { getSalutation } = useContext(AppContext);
    const confirmation = useConfirmation();

    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [modal, setModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [person, setPerson] = useState(false);
    const [message, setMessage] = useState(false);
    const [editMessage, setEditMessage] = useState(false);


    const confirmDeletePerson = (person) => {
        if (users.length == 1) {
            var text = <>
                <p>Durch die Löschung des letzten Benutzers, werden alle gebuchten Termine automatisch storniert.</p>
                <p>Soll der Benutzer {person.FirstName} {person.LastName} wirklich unwiderruflich gelöscht werden?</p>
            </>;
            confirmation.openModal("Benutzer löschen", text, () => { handleDeletePerson(person) }, "Ja", "Nein")
        }
        else {
            confirmation.openModal("Benutzer löschen", "Soll der Benutzer " + person.FirstName + " " + person.LastName + " wirklich unwiderruflich gelöscht werden?", () => { handleDeletePerson(person) }, "Ja", "Nein")
        }
    };

    const handleDeletePerson = async (person) => {
        try {
            let result = await request("/Operator/DeleteUser", { UserID: person.UserID, OrganisationID: id });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                setUsers((persons) => {
                    return persons.filter(p => p.UserID != person.UserID);
                });
                toast.success("Benutzer erfolgreich gelöscht.");
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    };

    const handleSubmit = async (values, editMode) => {
        if (editMode) {
            editUser(values);
        }
        else {
            addUser(values);
        }
    };

    const addUser = async (values) => {
        try {
            setMessage("");
            const result = await request("/Operator/AddUser", { OrganisationID: id, SalutationID: values.salutation, Title: values.title, FirstName: values.firstname, LastName: values.lastname, Email: values.email, PhoneNo: values.phone, Username: values.username, IsAdmin: values.admin });
            const json = result.data;

            if (json.r == 1) {
                toast.success("Benutzer erfolgreich hinzugefügt.");
                updatePersons(json.user);
                setModal(false);
            }
            else {
                setMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setMessage("Es ist ein Fehler aufgetreten.");
        }
    };

    const editUser = async (values) => {
        try {
            setEditMessage("");
            const result = await request("/Operator/EditUser", { UserID: person.UserID, OrganisationID: id, SalutationID: values.salutation, Title: values.title, FirstName: values.firstname, LastName: values.lastname, Email: values.email, PhoneNo: values.phone, Username: values.username, IsAdmin: values.admin });
            const json = result.data;

            if (json.r == 1) {
                toast.success("Benutzer erfolgreich bearbeitet.");
                updatePersons(json.user);
                setPerson(false);
                setEditModal(false);
            }
            else {
                setEditMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setEditMessage("Es ist ein Fehler aufgetreten.");
        }
    };

    const updatePersons = (person) => {
        setUsers(oldUsers => {
            const temp = [...oldUsers];
            const foundUser = temp.find(u => u.UserID == person.UserID);

            if (foundUser) {
                foundUser.SalutationID = person.SalutationID;
                foundUser.SalutationDescription = getSalutation(foundUser.SalutationID);
                foundUser.Title = person.Title;
                foundUser.FirstName = person.FirstName;
                foundUser.LastName = person.LastName;
                foundUser.Email = person.Email;
                foundUser.PhoneNo = person.PhoneNo;
                foundUser.IsAdmin = person.IsAdmin;
            }
            else {
                person.SalutationDescription = getSalutation(person.SalutationID);
                temp.push(person);
            }

            return temp;
        });
    };


    const columns = [
        columnHelper.accessor('Username', {
            header: () => <span>Benutzername</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('SalutationDescription', {
            header: () => <span>Anrede</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Title', {
            header: () => <span>Titel</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('FirstName', {
            header: () => <span>Vorname</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('LastName', {
            header: () => <span>Nachname</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Email', {
            header: () => <span>E-Mail</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('PhoneNo', {
            header: () => <span>Telefon</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('OrganisationID', {
            size: 195,
            header: () => <span></span>,
            cell: info => {
                return <div className="text-end">
                    <Button type="button" variant="primary" size="sm" onClick={(e) => { setEditModal(true); setPerson(info.row.original); }} className="ms-2">Bearbeiten</Button>
                    <Button type="button" variant="danger" size="sm" onClick={(e) => confirmDeletePerson(info.row.original)} className="ms-2">Löschen</Button>
                </div>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ]

    const reactTable = useReactTable({
        data: users || [],
        columns,
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <>
            <div className="text-end">
                <Button type="button" variant="primary" onClick={(e) => setModal(true)}>Benutzer hinzufügen</Button>
            </div>
            <Table striped>
                <thead>
                    {reactTable.getHeaderGroups().map(headerGroup => (
                        <TableHeader key={headerGroup.id} table={reactTable} headerGroup={headerGroup} flexRender={flexRender} />
                    ))}
                </thead>
                <tbody>
                    {reactTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => {
                                return (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
            <PersonModal modal={modal} closeModal={() => { setModal(false); setPerson(false); }} onSubmit={handleSubmit} message={message} editMode={false} />
            <PersonModal modal={editModal} closeModal={() => { setEditModal(false); setPerson(false); }} person={person} onSubmit={handleSubmit} message={editMessage} editMode={true} />
        </>
    );
}

function Contactpersons({ persons, setPersons, id }) {
    const { request } = useRequest();

    const { getSalutation } = useContext(AppContext);
    const confirmation = useConfirmation();

    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [addModal, setAddModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [person, setPerson] = useState(false);
    const [addMessage, setAddMessage] = useState('');
    const [editMessage, setEditMessage] = useState('');

    const confirmDeletePerson = (person) => {
        if (person.DefaultPerson) {
            return toast.error("Die Standard-Kontaktperson kann nicht gelöscht werden.");
        }
        confirmation.openModal("Kontaktperson löschen?", "Soll die Kontaktperson " + person.FirstName + " " + person.LastName + " wirklich unwiderruflich gelöscht werden?", () => { deletePerson(person) }, "Ja", "Nein")
    }


    const addPerson = async (values) => {
        try {
            setAddMessage("");
            const result = await request("/Operator/AddContactperson", { OrganisationID: id, SalutationID: values.salutation, Title: values.title, FirstName: values.firstname, LastName: values.lastname, Email: values.email, PhoneNo: values.phone, DefaultPerson: values.default });
            const json = result.data;

            if (json.r == 1) {
                toast.success("Referent erfolgreich hinzugefügt.");
                updatePersons(json.person);
                setAddModal(false);
            }
            else {
                setAddMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setAddMessage("Es ist ein Fehler aufgetreten.");
        }
    };
    const editPerson = async (values) => {
        try {
            setEditMessage("");
            const result = await request("/Operator/EditContactperson", {
                OrganisationID: id,
                SalutationID: values.salutation,
                Title: values.title,
                FirstName: values.firstname,
                LastName: values.lastname,
                Email: values.email,
                PhoneNo: values.phone,
                DefaultPerson: values.default,
                ContactPersonID: person.ContactPersonID
            });
            const json = result.data;

            if (json.r == 1) {
                toast.success("Referent erfolgreich gespeichert.");
                updatePersons(json.person);
                setEditModal(false);
                setPerson(false);
            }
            else {
                setEditMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setEditMessage("Es ist ein Fehler aufgetreten.");
        }
    };

    const deletePerson = async (person) => {
        try {
            let result = await request("/Operator/DeleteContactperson", { person });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                if (person.DefaultPerson) {
                    setPersons((persons) => {
                        return persons.map(p => {
                            if (p.ContactPersonID == person.ContactPersonID) {
                                p.Title = "";
                                p.FirstName = "";
                                p.LastName = "";
                                p.Email = "";
                                p.PhoneNo = "";
                            }
                            return p;
                        });
                    });
                }
                else {
                    setPersons((persons) => {
                        return persons.filter(p => p.ContactPersonID != person.ContactPersonID);
                    });
                }
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    }

    const updatePersons = (person) => {
        setPersons((items) => {
            if (person.DefaultPerson) {
                for (var i = 0; i < items.length; i++) {
                    items[i].DefaultPerson = false;
                }
            }

            var i = items.findIndex(item => item.ContactPersonID == person.ContactPersonID);
            if (i > -1) {
                items[i] = person;
                items[i].SalutationDescription = getSalutation(items[i].SalutationID);
            } else {
                person.SalutationDescription = getSalutation(person.SalutationID);
                items.push(person);
            }

            return [...items];
        });
    }


    const columns = [
        columnHelper.accessor('SalutationDescription', {
            header: () => <span>Anrede</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Title', {
            header: () => <span>Titel</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('FirstName', {
            header: () => <span>Vorname</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('LastName', {
            header: () => <span>Nachname</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Email', {
            header: () => <span>E-Mail</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('PhoneNo', {
            header: () => <span>Telefon</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('DefaultPerson', {
            header: () => <span>Standard</span>,
            cell: info => info.getValue() ? <div className="text-center"><i className="fas fa-lg fa-check"></i></div> : '',
            enableColumnFilter: false,
        }),
        columnHelper.accessor('OrganisationID', {
            size: 195,
            header: () => <span></span>,
            cell: info => {
                return <div className="text-end">
                    <Button type="button" variant="primary" size="sm" onClick={(e) => { setPerson(info.row.original); setEditModal(true); }} className="ms-2">Bearbeiten</Button>
                    <Button type="button" variant="danger" size="sm" onClick={(e) => confirmDeletePerson(info.row.original)} className="ms-2">Löschen</Button>
                </div>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ]

    const reactTable = useReactTable({
        data: persons || [],
        columns,
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <>
            <div className="text-end">
                <Button type="button" variant="primary" onClick={(e) => setAddModal(true)}>Hinzufügen</Button>
            </div>
            <Table striped>
                <thead>
                    {reactTable.getHeaderGroups().map(headerGroup => (
                        <TableHeader key={headerGroup.id} table={reactTable} headerGroup={headerGroup} flexRender={flexRender} />
                    ))}
                </thead>
                <tbody>
                    {reactTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => {
                                return (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
            <AddContactperson modal={addModal} setModal={setAddModal} addPerson={addPerson} contactLabel="Referent" message={addMessage} />
            <EditContactperson modal={editModal} setModal={setEditModal} editPerson={editPerson} contactLabel="Referent" person={person} setPerson={setPerson} message={editMessage} />
        </>
    );
}

export default SchoolOperator;